'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Mail, Lock, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  identifier: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onForgotPassword: () => void;
}

export default function LoginForm({ onSwitchToRegister, onForgotPassword }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginStatus, setLoginStatus] = useState<null | 'pending' | 'rejected' | 'suspended'>(null);
  const router = useRouter();
  const { signIn } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setLoginStatus(null);
    try {
      const result = await signIn(data.identifier, data.password);
      const profile = result?.profile;

      if (profile?.role === 'admin') {
        toast.success('Welcome back, Admin!');
        router.push('/admin-dashboard');
      } else if (profile?.status === 'approved') {
        toast.success('Welcome back!');
        router.push('/member-dashboard');
      } else if (profile?.status === 'pending') {
        setLoginStatus('pending');
      } else if (profile?.status === 'rejected') {
        setLoginStatus('rejected');
      } else if (profile?.status === 'suspended') {
        setLoginStatus('suspended');
      } else {
        toast.success('Welcome back!');
        router.push('/member-dashboard');
      }
    } catch (error: any) {
      const msg = error?.message ?? '';
      const msgLower = msg.toLowerCase();
      if (
        msgLower.includes('invalid') ||
        msgLower.includes('credentials') ||
        msgLower.includes('email not confirmed') ||
        msgLower.includes('invalid login') ||
        msgLower.includes('wrong password') ||
        msgLower.includes('user not found') ||
        msgLower.includes('no account found')
      ) {
        toast.error('Invalid email/mobile or password. Please try again.');
      } else {
        toast.error(msg || 'Sign in failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
        <p className="text-sm text-muted-foreground">Sign in to your gym account</p>
      </div>

      {loginStatus === 'pending' && (
        <div className="mb-6 p-4 rounded-xl bg-warning/10 border border-warning/30">
          <p className="text-sm font-semibold text-warning mb-1">Account Pending Approval</p>
          <p className="text-xs text-muted-foreground">
            Your account is currently waiting for gym admin approval. You will be able to check in once your account is approved.
          </p>
        </div>
      )}

      {loginStatus === 'rejected' && (
        <div className="mb-6 p-4 rounded-xl bg-destructive/10 border border-destructive/30">
          <p className="text-sm font-semibold text-destructive mb-1">Account Rejected</p>
          <p className="text-xs text-muted-foreground">
            Your registration was not approved. Please contact the gym admin for more information.
          </p>
        </div>
      )}

      {loginStatus === 'suspended' && (
        <div className="mb-6 p-4 rounded-xl bg-destructive/10 border border-destructive/30">
          <p className="text-sm font-semibold text-destructive mb-1">Account Suspended</p>
          <p className="text-xs text-muted-foreground">
            Your account has been temporarily suspended. Please contact the gym admin.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div>
          <label htmlFor="login-identifier" className="block text-sm font-medium text-foreground mb-1.5">
            Email or Mobile Number
          </label>
          <div className="relative">
            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="login-identifier"
              type="text"
              placeholder="arjun@example.com or 9876543210"
              className={`input-field pl-10 ${errors.identifier ? 'border-accent focus:ring-accent' : ''}`}
              {...register('identifier', {
                required: 'Email or mobile number is required',
                validate: (value) => {
                  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
                  const isMobile = /^[0-9]{10,15}$/.test(value.replace(/[\s\-\+\(\)]/g, ''));
                  if (!isEmail && !isMobile) {
                    return 'Enter a valid email or mobile number';
                  }
                  return true;
                },
              })}
            />
          </div>
          {errors.identifier && (
            <p className="mt-1.5 text-xs text-accent">{errors.identifier.message}</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="text-sm font-medium text-foreground">
              Password
            </label>
            <button type="button" onClick={onForgotPassword} className="text-xs text-primary hover:underline">
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className={`input-field pl-10 pr-10 ${errors.password ? 'border-accent focus:ring-accent' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-accent">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            id="remember-me"
            type="checkbox"
            className="w-4 h-4 rounded border-border bg-input accent-primary"
            {...register('rememberMe')}
          />
          <label htmlFor="remember-me" className="text-sm text-muted-foreground">
            Remember me for 30 days
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full flex items-center justify-center gap-2 py-3.5"
        >
          {isLoading ? (
            <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : (
            <LogIn size={18} />
          )}
          {isLoading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        New member?{' '}
        <button onClick={onSwitchToRegister} className="text-primary font-semibold hover:underline">
          Register here
        </button>
      </p>
    </div>
  );
}